






import { defineComponent, useContext, useRoute } from '@nuxtjs/composition-api'
import { MappedProduct, MappedSKU } from '@/types/product/productTypes'
import { usePdpStore } from '~/store/pdp'
import { useCloudflareStore } from '~/store'
import { mapSupportingDataAttributeToValue } from '@/common/utils/product/product'
import { PRODUCT_STATUS } from '@/common/utils/product/constants'
import { centsToPrice, formatNumber } from '@/common/utils/numbers'
import { stripHTMLTags } from '@/common/utils/characters'

export default defineComponent({
  props: {
    product: {
      type: Object as () => MappedProduct,
      required: true,
    },
  },
  setup(props) {
    const { $config } = useContext()
    const { citationsCount } = usePdpStore()
    const cloudflareStore = useCloudflareStore()
    const route = useRoute()

    let fullUrl = ''

    if (process.server) {
      fullUrl = process.static ? '' : `/${route.value.fullPath}`
    }
    if (process.client) {
      fullUrl = window.location.pathname
    }

    const product = props.product
    const productType = product.productType

    const image = product.attributes.images.map((image) => {
      let url = `${$config.cst.mediaUrl}/product/image/${image.filename}`
      if (
        cloudflareStore.images[image['dam-media-id']] &&
        cloudflareStore.images[image['dam-media-id']].variants.PUBLIC
      ) {
        url = cloudflareStore.images[image['dam-media-id']].variants.PUBLIC
      }
      return url
    })
    const listOfApplicationsWithImages = product.attributes.lots?.lot.applications?.application
      .map((app) => {
        const code = []
        if (app.figures) {
          code.push(app.group)
        }
        return code
      })
      .join(',')

    const buildOfferObject = (sku: MappedSKU) => {
      return {
        '@type': 'Offer',
        price: `${formatNumber(
          centsToPrice(sku.priceForCurrentCountry?.centAmount, sku.priceForCurrentCountry?.fractionDigits),
          sku.priceForCurrentCountry?.fractionDigits
        )}`,
        priceCurrency: `${sku.priceForCurrentCountry?.currencyCode}`,
        sku: `${sku.sku}`,
        availability: 'http://schema.org/InStock',
        itemCondition: 'http://schema.org/NewCondition',
        url: `${fullUrl}`,
      }
    }

    const generateIsRelatedTo = () => {
      const listOfRelateds = product.recommendedProducts?.map((v) => v.productUrl) ?? ''
      return listOfRelateds.length > 1 ? listOfRelateds : listOfRelateds[0]
    }

    const getTargetsForDescription = () => {
      const targets = product.targets?.map((t) => t['target-name'])
      return targets ? targets.join('/') : ''
    }

    const getCitationDescription = () => {
      if (citationsCount > 0) {
        return `Cited in ${citationsCount} publication${citationsCount > 1 ? 's' : ''}.`
      }
      return ''
    }

    const getApplicationDescription = () => {
      const applications = product.attributes.lots?.lot.applications?.application
      if (applications) {
        const append = applications.length > 3 ? 'codeForDisplay' : 'method'
        const apps = applications.map((a) => a[append]).join()
        return ` Validated for ${apps}.`
      }
      return ''
    }

    const getSkuDescription = () => {
      const skuLength = Object.values(product.skuData).length
      if (skuLength > 1) {
        return ` Available in ${skuLength} sizes.`
      }
      return ''
    }

    const getShipDescription = () => {
      return ` Highly specific and rigorously validated in-house, ${product.name.replace(
        'mAb',
        'Monoclonal Antibody'
      )} (CST #${product.key}) is ready to ship`
    }

    const getRestOfDescription = () => {
      if (productType !== 'Monoclonal Antibody' && productType !== 'Polyclonal Antibody') {
        return ' in the research area'
      }
      return `. ${getCitationDescription()}${getApplicationDescription()}${getSkuDescription()}${getShipDescription()}`
    }

    const generateDescriptionForPDPSchema = () => {
      return `${productType} for studying ${getTargetsForDescription()}${getRestOfDescription()}.`
    }

    const generateOffers = () => {
      const status = product.attributes.statusDates.status
      if (
        status !== PRODUCT_STATUS.DISCONTINUED &&
        status !== PRODUCT_STATUS.PENDING &&
        status !== PRODUCT_STATUS.OBSOLETE &&
        status !== PRODUCT_STATUS.PRE_RELEASED
      ) {
        const listOfOffers = Object.values(product.skuData).map((sku) => buildOfferObject(sku))
        return listOfOffers.length > 1 ? listOfOffers : listOfOffers[0]
      }
    }

    const jsonLd = {
      '@context': 'http://schema.org/',
      '@type': 'Product',
      name: `${stripHTMLTags(product.name)}`,
      category: `${stripHTMLTags(product.productCategory as string)}`,
      productID: `${product.key}`,
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'SENSITIVITY',
          value: `${product.attributes.lots?.lot.sensitivity ?? ''}`,
        },
        {
          '@type': 'PropertyValue',
          name: 'MW(kDa)',
          value: `${product.attributes.observedMolecularWeight ?? ''}`,
        },
        {
          '@type': 'PropertyValue',
          name: 'SOURCE',
          value: `${product.attributes.host ?? ''}`,
        },
        {
          '@type': 'PropertyValue',
          name: 'Product Citations',
          value: `${citationsCount}`,
        },
      ],
      image,
      description: generateDescriptionForPDPSchema(),
      isRelatedTo: generateIsRelatedTo() ?? '',
      offers: generateOffers(),
    }

    if (product.attributes.lots?.lot.reactivity) {
      jsonLd.additionalProperty.push({
        '@type': 'PropertyValue',
        name: 'REACTIVITY',
        value: mapSupportingDataAttributeToValue(product, 'reactivity', ',') ?? '',
      })
    }

    if (product.attributes.lots?.lot.applications) {
      jsonLd.additionalProperty.push({
        '@type': 'PropertyValue',
        name: 'APPLICATIONS',
        value: listOfApplicationsWithImages ?? '',
      })
    }

    return {
      jsonLd: JSON.stringify(jsonLd, undefined, 2),
    }
  },
})
